/**
 * Задачи нового бэка — группа `/v2/tasks*` (74 маршрута, Postgres-native).
 *
 * Живёт рядом со старым `tasks.ts`, а не вместо него: пока флаг
 * `VITE_USE_V2` выключен, приложение работает на legacy-группе `/tasks`,
 * которая в новом бэке удалена целиком.
 *
 * Отличия контракта от legacy, из-за которых нельзя переиспользовать старый
 * слой (подробности — docs/v2-migration-plan.md §5):
 *  - все идентификаторы UUID, а не int;
 *  - параметры camelCase, а не SCREAMING_SNAKE;
 *  - пагинация `limit`/`offset`, ответ `{ result, total }` без `hasMore`;
 *  - в задаче 24 поля вместо 66, остальное — сабресурсами и `/card`.
 */
import { http } from '@/shared/api';
import type { SchemaV2 } from '@/shared/api';

export type TaskV2ListItem =
  SchemaV2<'internal_tasks_handler.taskListItemResponse'>;
export type TaskV2Detail =
  SchemaV2<'internal_tasks_handler.taskDetailResponse'>;
export type TaskV2Card = SchemaV2<'internal_tasks_handler.taskCardResponse'>;
export type TaskV2Write = SchemaV2<'internal_tasks_handler.taskWriteRequest'>;
export type TaskV2Board = SchemaV2<'internal_tasks_handler.boardResponse'>;
export type TaskV2CalendarItem =
  SchemaV2<'internal_tasks_handler.calendarItemResponse'>;
export type TaskV2Patch = SchemaV2<'internal_tasks_handler.taskPatchRequest'>;

/** Коды массовых действий. В спеке `action` — просто строка, перечень бэк
 *  прислал отдельно, поэтому сужаем его здесь. */
export type TaskV2BulkAction = 'patch' | 'complete' | 'delete' | 'assign';

/** Тело `POST /v2/tasks/bulk`: `patch` — поля `taskPatchRequest`, `assign` —
 *  `userIds` + `role`, `complete` и `delete` идут без параметров. */
export type TaskV2BulkRequest = Omit<
  SchemaV2<'internal_tasks_handler.bulkActionRequest'>,
  'action' | 'role'
> & {
  action: TaskV2BulkAction;
  role?: TaskV2AssigneeRole;
};
export type TaskV2BulkResponse =
  SchemaV2<'internal_tasks_handler.bulkActionResponse'>;

/** Больше задач за раз бэк не примет (MaxBulkTasks). */
export const MAX_BULK_TASKS_V2 = 200;

/** Параметры `GET /v2/tasks`. Имена — ровно как в спеке. */
export type FetchTasksV2Params = {
  responsibleId?: string;
  groupId?: string;
  stageId?: string;
  status?: number;
  parentId?: string;
  /** Формат дат — `YYYY-MM-DD`, не RFC3339. */
  deadlineFrom?: string;
  deadlineTo?: string;
  createdBy?: string;
  search?: string;
  excludeCompleted?: boolean;
  /** Задачи, где ответственный состоит в отделе (UUID отдела). */
  departmentId?: string;
  /** Только просроченные: дедлайн прошёл, задача не завершена. */
  overdue?: boolean;
  /** Только задачи с открытым запросом помощи в чате. */
  help?: boolean;
  sort?:
    | 'createdAt'
    | 'updatedAt'
    | 'deadline'
    | 'title'
    | 'status'
    | 'priority';
  order?: 'asc' | 'desc';
  linkedObjectId?: string;
  /** По умолчанию 50, максимум 100 — больше бэк не отдаёт. */
  limit?: number;
  offset?: number;
};

export type TasksV2Page = {
  result: TaskV2ListItem[];
  total: number;
};

/* ----------------------------------------------------------------- list --- */

export async function fetchTasksV2(
  params: FetchTasksV2Params = {}
): Promise<TasksV2Page> {
  const { data } = await http.get<{
    result?: TaskV2ListItem[];
    total?: number;
  }>('/v2/tasks', { params });

  return { result: data.result ?? [], total: data.total ?? 0 };
}

export async function fetchTaskV2(id: string): Promise<TaskV2Detail> {
  const { data } = await http.get<TaskV2Detail>(
    `/v2/tasks/${encodeURIComponent(id)}`
  );

  return data;
}

/** Карточка одним запросом: задача + счётчики + план/факт + команда. */
export async function fetchTaskCardV2(id: string): Promise<TaskV2Card> {
  const { data } = await http.get<TaskV2Card>(
    `/v2/tasks/${encodeURIComponent(id)}/card`
  );

  return data;
}

/* ---------------------------------------------------------------- write --- */

export async function createTaskV2(
  payload: TaskV2Write
): Promise<TaskV2Detail> {
  const { data } = await http.post<TaskV2Detail>('/v2/tasks', payload);

  return data;
}

/**
 * Частичная правка. Тип — `taskPatchRequest`, а не `taskWriteRequest`: у патча
 * есть флаги `clearDeadline` и `clearRemindAt`, которыми снимают срок и
 * напоминание (пустой строки у времени нет, поэтому нужны отдельные флаги).
 */
export async function updateTaskV2(
  id: string,
  payload: TaskV2Patch
): Promise<TaskV2Detail> {
  const { data } = await http.patch<TaskV2Detail>(
    `/v2/tasks/${encodeURIComponent(id)}`,
    payload
  );

  return data;
}

export async function deleteTaskV2(id: string): Promise<void> {
  await http.delete(`/v2/tasks/${encodeURIComponent(id)}`);
}

/** Перенос карточки между колонками доски. */
export function moveTaskToStageV2(id: string, stageId: string) {
  return updateTaskV2(id, { stageId });
}

/** Смена статуса — когда доска построена по статусам, а не по стадиям. */
export function setTaskStatusV2(id: string, status: number) {
  return updateTaskV2(id, { status });
}

export type TaskV2Clone = SchemaV2<'internal_tasks_handler.cloneTaskRequest'>;

/** Копия задачи. Тело необязательное — им переопределяют поля клона. */
export async function cloneTaskV2(
  id: string,
  payload: TaskV2Clone = {}
): Promise<string> {
  const { data } = await http.post<{ id?: string }>(
    `/v2/tasks/${encodeURIComponent(id)}/clone`,
    payload
  );

  return data.id ?? '';
}

/* ------------------------------------------------------------- elapsed --- */

export type TaskV2Elapsed = SchemaV2<'internal_tasks_handler.elapsedResponse'>;

/** Списания времени по задаче — таб «Время» на детальной странице. */
export async function fetchTaskElapsedV2(id: string): Promise<TaskV2Elapsed[]> {
  const { data } = await http.get<{ result?: TaskV2Elapsed[] }>(
    `/v2/tasks/${encodeURIComponent(id)}/elapsed`
  );

  return data.result ?? [];
}

export type TaskV2ElapsedWrite =
  SchemaV2<'internal_tasks_handler.elapsedWriteRequest'>;

/**
 * Ручное списание времени. `dateStart`, `dateStop` и `seconds` в контракте
 * обязательны, а вьюшка спрашивает только длительность — интервал откладываем
 * назад от текущего момента.
 */
export async function createTaskElapsedV2(
  id: string,
  payload: TaskV2ElapsedWrite
): Promise<string> {
  const { data } = await http.post<{ id?: string }>(
    `/v2/tasks/${encodeURIComponent(id)}/elapsed`,
    payload
  );

  return data.id ?? '';
}

/* --------------------------------------------------------------- таймер --- */

export type TaskV2TimerStatus =
  SchemaV2<'internal_tasks_handler.timerStatusResponse'>;

const timer = (id: string) => `/v2/tasks/${encodeURIComponent(id)}/timer`;

/** Статус таймера задачи: идёт ли он и сколько уже натикало. */
export async function fetchTaskTimerV2(id: string): Promise<TaskV2TimerStatus> {
  const { data } = await http.get<TaskV2TimerStatus>(timer(id));

  return data;
}

/** Запустить или возобновить — маршрут один на оба случая. */
export async function startTaskTimerV2(id: string): Promise<TaskV2TimerStatus> {
  const { data } = await http.post<TaskV2TimerStatus>(`${timer(id)}/start`);

  return data;
}

export async function pauseTaskTimerV2(id: string): Promise<TaskV2TimerStatus> {
  const { data } = await http.post<TaskV2TimerStatus>(`${timer(id)}/pause`);

  return data;
}

/**
 * Остановить и зафиксировать время: в отличие от паузы бэк закрывает интервал
 * и пишет запись в `/elapsed` — её и возвращает.
 */
export async function stopTaskTimerV2(id: string): Promise<TaskV2Elapsed> {
  const { data } = await http.post<TaskV2Elapsed>(`${timer(id)}/stop`);

  return data;
}

/* ----------------------------------------------------------- assignees --- */

/**
 * Соисполнители и наблюдатели. В `taskWriteRequest` их полей нет — состав
 * команды пишется отдельным маршрутом, по одному человеку за запрос, уже
 * после того как задача создана.
 */
export type TaskV2AssigneeRole = 'accomplice' | 'auditor';

export async function assignTaskUserV2(
  taskId: string,
  userId: string,
  role: TaskV2AssigneeRole
): Promise<void> {
  await http.post(`/v2/tasks/${encodeURIComponent(taskId)}/assignees`, {
    userId,
    role,
  });
}

export async function unassignTaskUserV2(
  taskId: string,
  userId: string
): Promise<void> {
  await http.delete(
    `/v2/tasks/${encodeURIComponent(taskId)}/assignees/${encodeURIComponent(userId)}`
  );
}

/* --------------------------------------------------------------- board --- */

/**
 * Доска задач. `groupId` необязателен: без него бэк строит колонки не по
 * стадиям группы, а по шести фиксированным статусам — и `stageId` в таких
 * колонках `null`, двигать карточки надо сменой статуса.
 */
export async function fetchTaskBoardV2(params: {
  groupId?: string;
  itemsPerColumn?: number;
}): Promise<TaskV2Board> {
  const { data } = await http.get<TaskV2Board>('/v2/tasks/board', { params });

  return data;
}

/* ------------------------------------------------------------ calendar --- */

export async function fetchTaskCalendarV2(params: {
  deadlineFrom: string;
  deadlineTo: string;
  responsibleId?: string;
  groupId?: string;
}): Promise<TaskV2CalendarItem[]> {
  const { data } = await http.get<{ result?: TaskV2CalendarItem[] }>(
    '/v2/tasks/calendar',
    { params }
  );

  return data.result ?? [];
}

/* ---------------------------------------------------------------- bulk --- */

export async function bulkTasksV2(
  payload: TaskV2BulkRequest
): Promise<TaskV2BulkResponse> {
  const { data } = await http.post<TaskV2BulkResponse>(
    '/v2/tasks/bulk',
    payload
  );

  return data;
}

/* ------------------------------------------------------------- actions --- */

export async function completeTaskV2(id: string): Promise<void> {
  await http.post(`/v2/tasks/${encodeURIComponent(id)}/complete`);
}

export async function renewTaskV2(id: string): Promise<void> {
  await http.post(`/v2/tasks/${encodeURIComponent(id)}/renew`);
}

export async function approveTaskV2(id: string): Promise<void> {
  await http.post(`/v2/tasks/${encodeURIComponent(id)}/approve`);
}

export async function rejectTaskV2(id: string, reason?: string): Promise<void> {
  await http.post(
    `/v2/tasks/${encodeURIComponent(id)}/reject`,
    reason ? { reason } : {}
  );
}
